import { StructureSdf, volumeMm3 } from '~/domain/structures/structureSdf';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';

/**
 * A structure that might be the patient outline: its identity, and a way to
 * resolve the field it holds.
 *
 * The field is resolved on demand, because resolving it can cost a
 * full-volume rasterisation — the engine builds a structure's field only when
 * something asks for one. {@link findBodyRoi} asks only for the structures
 * its type and name tests leave standing, and at most once each, so looking
 * for a body in a set of forty structures costs one or two fields, not forty.
 * Returns null when the structure has no field to read.
 */
export interface BodyCandidateRoi {
    readonly name: string;
    readonly interpretedType?: RoiInterpretedType;
    readonly sdf: () => StructureSdf | null;
}

/** The structure the couch top is read from, with its field resolved. */
export interface BodyRoi {
    readonly name: string;
    readonly sdf: StructureSdf;
}

/** Names a patient outline goes by when nobody typed it as EXTERNAL. */
const BODY_NAMES = /^\s*(body|external|outer\s*contour|patient)\s*$/i;

/**
 * Smallest volume a NAMED candidate must enclose, in mm³ — one litre.
 *
 * Only the name-matched path is held to it. An EXTERNAL-typed structure is
 * a declaration that someone made deliberately and is taken at its word; a
 * name match is a guess, and this is what keeps a 2 ml marker someone
 * called "Body" from being read as a patient. A head is several litres, so
 * the bar clears any real outline while rejecting anything incidental.
 */
const MIN_BODY_VOLUME_MM3 = 1_000_000;

/**
 * The structure to read the couch top off, or null when the set has none
 * worth trusting.
 *
 * EXTERNAL first, whatever it is called: the interpreted type is the
 * DICOM-level statement that this structure IS the patient outline, and a
 * set that carries one has already had the question answered. Only when
 * none is typed does this fall back to the names a body contour goes by,
 * and then only for a candidate large enough to be a patient — because by
 * then it is guessing from a string.
 *
 * A candidate with no field is no use to the reading that follows,
 * whatever it is called, so it is not offered as one.
 */
export function findBodyRoi(rois: readonly BodyCandidateRoi[]): BodyRoi | null {
    for (const roi of rois) {
        if (roi.interpretedType !== RoiInterpretedType.EXTERNAL) {
            continue;
        }
        const sdf = roi.sdf();
        if (sdf) {
            return { name: roi.name, sdf };
        }
    }
    for (const roi of rois) {
        // An EXTERNAL-typed structure was already asked for its field above,
        // and had none; asking again would repeat the cost for the same null.
        if (
            roi.interpretedType === RoiInterpretedType.EXTERNAL ||
            !BODY_NAMES.test(roi.name)
        ) {
            continue;
        }
        const sdf = roi.sdf();
        // The volume is of the field, not of a contour sequence that may
        // not exist.
        if (sdf && volumeMm3(sdf) >= MIN_BODY_VOLUME_MM3) {
            return { name: roi.name, sdf };
        }
    }
    return null;
}
